import { Add, Delete, Edit } from "@mui/icons-material"
import { Stack } from "@mui/material"
import type { GridColDef } from "@mui/x-data-grid-pro"
import { GridActionsCellItem } from "@mui/x-data-grid-pro"
import type { BiomeshOperationResponse } from "@phc/common/gen/threatAnalyticsGen"
import { useListOperationsBiomeshOperationListGet } from "@phc/common/gen/threatAnalyticsGen"
import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"
import { BaseGrid } from "../BaseGrid/BaseGrid"
import Button from "../Shared/Button"
import { DeleteOperationDialog } from "./DeleteOperationDialog"
import { OperationFormDialog } from "./OperationFormDialog"

export const OperationList: FC = () => {
  const { data: operations, isLoading } =
    useListOperationsBiomeshOperationListGet({
      include: "units,platforms,contacts",
    })

  const [formOpen, setFormOpen] = useState(false)
  const [editOperation, setEditOperation] = useState<
    BiomeshOperationResponse | undefined
  >()
  const [deleteOperation, setDeleteOperation] = useState<
    BiomeshOperationResponse | undefined
  >()

  const handleAdd = useCallback(() => {
    setEditOperation(undefined)
    setFormOpen(true)
  }, [])

  const handleEdit = useCallback((operation: BiomeshOperationResponse) => {
    setEditOperation(operation)
    setFormOpen(true)
  }, [])

  const handleCloseForm = useCallback(() => {
    setFormOpen(false)
    setEditOperation(undefined)
  }, [])

  const columns: GridColDef<BiomeshOperationResponse>[] = useMemo(
    () => [
      { field: "name", headerName: "Name", flex: 1 },
      {
        field: "units",
        headerName: "Units",
        flex: 1,
        valueGetter: (_value, row) =>
          row.units?.map(u => u.name).join(", ") ?? "",
      },
      {
        field: "platforms",
        headerName: "Nodes",
        flex: 1,
        valueGetter: (_value, row) =>
          row.platforms?.map(p => p.name).join(", ") ?? "",
      },
      {
        field: "contacts",
        headerName: "Contacts",
        flex: 1,
        valueGetter: (_value, row) =>
          row.contacts?.map(c => `${c.first_name} ${c.last_name}`).join(", ") ??
          "",
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 0.8,
        valueFormatter: (value: string | null) =>
          value ? new Date(value).toLocaleDateString() : "—",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 100,
        disableExport: true,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit"
            icon={<Edit />}
            label="Edit"
            onClick={() => handleEdit(row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label="Delete"
            onClick={() => setDeleteOperation(row)}
          />,
        ],
      },
    ],
    [handleEdit]
  )

  return (
    <>
      <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
          }}
        >
          <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
            Add Operation
          </Button>
        </Stack>
        <BaseGrid
          rows={operations ?? []}
          columns={columns}
          getRowId={row => row.operation_id}
          loading={isLoading}
          search
          showToolbar
          exported
          exportFileName="biomesh-operations"
          placeholder="Search operations..."
        />
      </Stack>
      <OperationFormDialog
        key={editOperation?.operation_id ?? "new"}
        open={formOpen}
        onClose={handleCloseForm}
        operation={editOperation}
      />
      {deleteOperation && (
        <DeleteOperationDialog
          open
          onClose={() => setDeleteOperation(undefined)}
          operation={deleteOperation}
        />
      )}
    </>
  )
}
